import { NaniumObject, Type } from "nanium/objects";
import { GeoJsonPointType } from "./types/geoJsonPoint.type";

// Offer category shown to the user. Named NextOffer* (module "next") to avoid
// confusion with the existing ConsultingOffer / ParticipationOffer / otherOffer
// concepts — see NEXT_MODULE_PLAN.md §3.1.
export enum NextOfferType {
  ausbildung = 'ausbildung',
  studium = 'studium',
  fsjbfd = 'fsjbfd',
}

// Identifies the sync run an offer belongs to. This is deliberately NOT the
// source API: Ausbildung and FSJ/BFD both come from BA APIs, but are separate
// runs with separate deletion grace and staleness tracking. The nightly
// delete-reconciliation only ever compares offers of the same syncJob.
export enum NextSyncJob {
  baAusbildungssuche = 'ba-ausbildungssuche',
  baJobsucheFsjBfd = 'ba-jobsuche-fsjbfd',
  baStudisu = 'ba-studisu',
}

// Mirrored external offer (Ausbildung / Studium / FSJ-BFD) with the minimal
// field set agreed with AGJF (data frugality): only what the app displays plus
// what the sync technically needs. Deliberately NO descriptions, dates,
// street addresses, logos or source metadata.
//
// There is intentionally no lastSeenAt field: a per-run write on every document
// would defeat restic's dedup on the 3-hourly mongodumps. The sync only writes
// documents that actually change (new / content changed / missed / reappeared).
export class NextOffer extends NaniumObject<NextOffer> {
  static strictDefault: boolean = true;

  @Type(String) id?: string;
  @Type(String) offerType?: NextOfferType;
  @Type(String) syncJob?: NextSyncJob;
  // Source identifier (absuche termin-id / jobsuche refnr / studisu id).
  // Unique together with syncJob; basis for upserts and delete reconciliation.
  @Type(String) externalId?: string;

  @Type(String) title?: string;
  @Type(String) providerName?: string;
  @Type(String) ort?: string;
  // Cleaned link to the original offer (tracking params stripped). Optional:
  // when missing, the app simply hides the "Zum Angebot" button.
  @Type(String) externalUrl?: string;
  // Ausbildung only: offer targets access via rehabilitation status (§66 BBiG /
  // Reha-Träger). Shown as "inklusives Angebot" badge, never used to exclude.
  @Type(Boolean) rehaFlag?: boolean;

  @Type(GeoJsonPointType) geo?: GeoJsonPointType;

  // Hash over the displayed fields; change detection for sources without a
  // usable modification date (studisu has none at all).
  @Type(String) contentHash?: string;

  @Type(Date) firstSeenAt?: Date;
  // Deletion grace: incremented when a successful run of this offer's syncJob
  // does not see the offer; reset to 0 (and active=true) on re-sighting.
  // >= 2 deactivates; hard delete once deactivatedAt is older than 30 days.
  @Type(Number) missedRuns?: number;
  @Type(Boolean) active?: boolean;
  @Type(Date) deactivatedAt?: Date;

  // TEMPORARY — filled per request by the geo query, never persisted.
  @Type(Number) distanceFromUser?: number;
}
